// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {useContext, useMemo} from 'react';
import cn from 'classnames';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import {App_context} from 'hola_app_context';
import {use_plans} from '../plans/plans';
import {Proxy_type} from '../../../../hola/pub/ui/pro/cp/orders';
import {use_qs_with_set} from '../../../../hola/pub/lib/use_qs';
import styles from './lobby_cards.module.less';
import {toolip_zones_availability} from 'hola_opt';
import {PROXY_NAMES} from './pricing_lobby';
import {Card} from './card';
import {Button} from '../../../../hola/pub/lib/button';
import Icon_circle_v from '../../img/icons/18/circle_v.svg';
import {get_new_link} from '../../../../hola/pub/lib/use_pathname';

const features = [
    'SOCKS5 support',
    '99.99% uptime',
    'Pool of 10,000 IPs',
    'Up to 20GB per proxy',
    'Flat fees, no hidden costs'
];

const Feature_item = ({text})=>{
    const {theme} = useContext(Settings_context);
    return <div className={styles.feature_item}>
      <Icon_circle_v />
      <p className={cn(theme.p14, theme.reg, theme.color_n600)}>
        {text}
      </p>
    </div>;
};

const Features_list = ()=>{
    return <div className={styles.features_container}>
      {features.map((feature, index)=>
        <Feature_item key={index} text={feature} />
      )}
    </div>;
};

const Mobile_banner = ()=>{
    const {theme} = useContext(Settings_context);
    return <div className={styles.mobile_connect_banner}>
      <p className={cn(theme.p16, theme.med, theme.color_n700)}>
        Need help finding the right proxy package for you?
      </p>
      <Button
        caption='Get in touch'
        color='black'
        round={false}
        size='sm'
        to='mailto:support@toolip.io'
        cls={styles.mobile_btn}
      />
    </div>;
};

const PROXY_ROUTES = {
    [Proxy_type.SDC]: '/pricing/shared-datacenter-proxies',
    [Proxy_type.DDC]: '/pricing/dedicated-datacenter-proxies',
    [Proxy_type.ISP]: '/pricing/global-isp-proxies',
    [Proxy_type.ISPPREM]: '/pricing/search-engine-proxies',
    [Proxy_type.RES]: '/pricing/rotating-residential-proxies'
};

const get_pricing_url = (type?: Proxy_type): string=>{
    if (!type)
        return get_new_link('/pricing');
    return PROXY_ROUTES[type] || get_new_link(`/pricing/${type}`);
};

const More_plans_card = ({type, last_amount})=>{
    const {theme} = useContext(Settings_context);
    const link_url = get_pricing_url(type);
    return <div className={cn(styles.more_plans_card, styles[type])}>
      <div className={styles.more_plans_content}>
        <div className={styles.more_plans_text}>
          <p className={cn(theme.p16, theme.med, theme.color_n700)}>
            Need more than {last_amount}?
          </p>
          <p className={cn(theme.p12, theme.reg, theme.color_n600)}>
            Explore full <span className={cn(theme.p12, theme.med)}>
              {PROXY_NAMES[type]}</span> pricing options
          </p>
        </div>
        <Button
          caption='More plans'
          color='transparent_black'
          round={false}
          size='xs'
          to={link_url}
          cls={styles.more_btn}
        />
      </div>
    </div>;
};

interface i_lobby_cards_props {
    selected_type: Proxy_type;
}

export const Lobby_cards = ({selected_type}: i_lobby_cards_props)=>{
    const {theme} = useContext(Settings_context);
    const {is_client} = useContext(App_context);
    const {qs} = use_qs_with_set();
    const {plans} = use_plans();
    const current_period = qs.period || '1w';
    const processed_data = useMemo(()=>{
        if (!is_client || !plans)
        {
            return {
                prices: [],
                limits: [],
                unit_prices: [],
                plan_ids: [],
                ips_or_gb_amount: [],
                sold_out: []
            };
        }
        let product_plans = [];
        if (plans[selected_type])
        {
            if (plans[selected_type][current_period] &&
               plans[selected_type][current_period].length > 0)
            {
                product_plans = plans[selected_type][current_period];
            }
            else
            {
                const available_periods = Object.keys(plans[selected_type]);
                if (available_periods.length > 0)
                    product_plans = plans[selected_type][available_periods[0]];
            }
        }
        if (!product_plans || product_plans.length == 0)
        {
            return {
                prices: [],
                limits: [],
                unit_prices: [],
                plan_ids: [],
                ips_or_gb_amount: [],
                sold_out: []
            };
        }
        const prices = [];
        const limits = [];
        const unit_prices = [];
        const plan_ids = [];
        const ips_or_gb_amount = [];
        const sold_out = [];
        for (let i = 0; i < product_plans.length; i++)
        {
            const plan = product_plans[i].plan;
            const amount = product_plans[i].ips_amount !== undefined ?
                product_plans[i].ips_amount : product_plans[i].limit;
            limits.push(`${product_plans[i].limit}`);
            prices.push(plan.price);
            plan_ids.push(plan.id);
            ips_or_gb_amount.push(amount);
            const unit_type =
                selected_type == Proxy_type.RES ? ' / GB' : ' / IP';
            unit_prices.push((plan.price / amount).toFixed(2) + unit_type);
            const is_sold_out = toolip_zones_availability &&
                toolip_zones_availability[selected_type] &&
                toolip_zones_availability[selected_type] < amount;
            sold_out.push(is_sold_out || false);
        }
        return {
            prices,
            limits,
            unit_prices,
            plan_ids,
            ips_or_gb_amount,
            sold_out
        };
    }, [plans, selected_type, current_period, is_client]);
    const is_all_sold_out = processed_data.sold_out &&
        processed_data.sold_out.length ?
        processed_data.sold_out.every(v=>v == true) : false;
    const display_cards = processed_data.plan_ids &&
        processed_data.plan_ids.length ?
        processed_data.plan_ids.slice(0, 5) : [];
    const last_amount = display_cards.length ?
        processed_data.ips_or_gb_amount[display_cards.length - 1] : 0;
    const show_free_trial = selected_type == Proxy_type.SDC;
    if (!is_client)
        return null;
    return <div className={styles.lobby_cards_container}>
      {is_all_sold_out &&
        <div className={styles.status_banner}>
          <p className={cn(theme.p14, theme.bold, theme.color_warn300)}>
            ALL SOLD OUT!
          </p>
        </div>
      }
      <div className={styles.cards_grid}>
        {show_free_trial && !is_all_sold_out &&
          <Card
            amount='FREE'
            price='$0 / IP'
            billed='Free for 3 days'
            plan_id='free'
            type={selected_type}
            is_free={true}
            is_all_sold_out={is_all_sold_out}
          />
        }
        {display_cards.map((plan_id, index)=>{
            const card_index = index;
            if (card_index >= processed_data.plan_ids.length)
                return null;
            const amount = processed_data.ips_or_gb_amount[card_index];
            const unit = selected_type == Proxy_type.RES ? 'GB' :
                amount == 1 ? 'proxy' : 'proxies';
            const bandwidth = selected_type !== Proxy_type.RES ?
                processed_data.limits[card_index] : null;
            return <Card
              key={plan_id}
              amount={`${amount} ${unit}`}
              price={`${processed_data.unit_prices[card_index]}`}
              billed={`$${processed_data.prices[card_index]} billed`}
              bandwidth={bandwidth}
              plan_id={plan_id}
              type={selected_type}
              is_popular={index == 2}
              is_sold_out={processed_data.sold_out[card_index]}
              is_all_sold_out={is_all_sold_out}
            />;
        })}
        {!is_all_sold_out && last_amount > 0 &&
          <More_plans_card
            type={selected_type}
            last_amount={`${last_amount} ${selected_type == Proxy_type.RES ?
              'GB' : 'IPs'}`}
          />
        }
      </div>
      <Features_list/>
      <Mobile_banner/>
    </div>;
};

